import * as React from 'react'
import { LedgerItem, LedgerItemColumnConfig, InvoiceColumnProperty, InvoiceColumns, LedgerItemProperty, ResourceRendererOptions, ResourceRendererResult } from '../../types'
import { ResourceTableHeader } from './ResourceTable'
import ResourceTableRow from './ResourceTableRow'
import ResourceTableRowData from './ResourceTableRowData'
import ResourceTableRowActions from './ResourceTableRowActions'
import { IActionListItem } from '../ActionList/ActionList'
import i18n from '../../translations'
import LocalStorage, { LocalStorageKey } from '../../LocalStorage'
import NumberFormatter from '../../utilities/NumberFormatter'
import moment from '../../utilities/Moment'
import showdown from '../../utilities/showdown'
import LedgerItemAvatar from '../Avatar/LedgerItemAvatar'

export const DEFAULT_INVOICE_COLUMNS: InvoiceColumnProperty[] = [
  'number',
  'contact',
  'issued_on',
  'due_on',
  'net_total',
  'amount'
]

const InvoiceColumnConfigs: Record<InvoiceColumnProperty, LedgerItemColumnConfig> = {
  id: {
    header: () => ({ title: i18n.t('LedgerItem::id') }),
    render: ({ ledgerItem }) => ledgerItem.id || '-'
  },
  contact: {
    header: () => ({ title: i18n.t('LedgerItem::client') }),
    render: ({ ledgerItem }) => ledgerItem.contact?.name || '-'
  },
  project: {
    header: () => ({ title: i18n.t('LedgerItem::project') }),
    render: ({ ledgerItem }) => ledgerItem.project?.name || '-'
  },
  number: {
    header: () => ({ title: i18n.t('LedgerItem::number') }),
    render: ({ ledgerItem }) => ledgerItem.number || '-'
  },
  subject: {
    header: () => ({ title: i18n.t('LedgerItem::subject') }),
    render: ({ ledgerItem }) => <div dangerouslySetInnerHTML={{ __html: showdown.makeHtml(ledgerItem.subject || '-') }} />
  },
  identifier: {
    header: () => ({ title: i18n.t('LedgerItem::identifier') }),
    render: ({ ledgerItem }) => ledgerItem.identifier || '-'
  },
  po_number: {
    header: () => ({ title: i18n.t('LedgerItem::po_number') }),
    render: ({ ledgerItem }) => ledgerItem.po_number || '-'
  },
  currency: {
    header: () => ({ title: i18n.t('LedgerItem::currency') }),
    render: ({ ledgerItem }) => ledgerItem.currency || '-'
  },
  issued_on: {
    header: () => ({ title: i18n.t('LedgerItem::issued_on') }),
    render: ({ ledgerItem, setting }) => ledgerItem.issued_on ? moment(ledgerItem.issued_on).format(setting.date_format) : '-'
  },
  due_on: {
    header: () => ({ title: i18n.t('LedgerItem::due_on') }),
    render: ({ ledgerItem, setting }) => ledgerItem.due_on ? moment(ledgerItem.due_on).format(setting.date_format) : '-'
  },
  paid_on: {
    header: () => ({ title: i18n.t('LedgerItem::paid_on') }),
    render: ({ ledgerItem, setting }) => ledgerItem.paid_on ? moment(ledgerItem.paid_on).format(setting.date_format) : '-'
  },
  booked_on: {
    header: () => ({ title: i18n.t('LedgerItem::booked_on') }),
    render: ({ ledgerItem, setting }) => ledgerItem.booked_on ? moment(ledgerItem.booked_on).format(setting.date_format) : '-'
  },
  credited_on: {
    header: () => ({ title: i18n.t('LedgerItem::credited_on') }),
    render: ({ ledgerItem, setting }) => ledgerItem.credited_on ? moment(ledgerItem.credited_on).format(setting.date_format) : '-'
  },
  sent_at: {
    header: () => ({ title: i18n.t('LedgerItem::sent_at') }),
    render: ({ ledgerItem, setting }) => ledgerItem.sent_at ? moment(ledgerItem.sent_at).format(setting.date_format) : '-'
  },
  sent_peppol: {
    header: () => ({ title: i18n.t('LedgerItem::sent_peppol') }),
    render: ({ ledgerItem, setting }) => ledgerItem.peppol_id ? i18n.t('LedgerItem::Yes') : i18n.t('LedgerItem::No')
  },
  viewed_at: {
    header: () => ({ title: i18n.t('LedgerItem::viewed_at') }),
    render: ({ ledgerItem, setting }) => ledgerItem.viewed_at ? moment(ledgerItem.viewed_at).format(setting.date_format) : '-'
  },
  subtotal: {
    header: () => ({ title: i18n.t('LedgerItem::subtotal'), align: 'right' }),
    render: ({ ledgerItem, setting }) => NumberFormatter.formatCurrency(ledgerItem.currency || setting.default_currency, setting.number_format, ledgerItem.subtotal),
  },
  discount: {
    header: () => ({ title: i18n.t('LedgerItem::discount'), align: 'right' }),
    render: ({ ledgerItem, setting }) => NumberFormatter.formatCurrency(ledgerItem.currency || setting.default_currency, setting.number_format, ledgerItem.discount)
  },
  net_total: {
    header: () => ({ title: i18n.t('LedgerItem::net_total'), align: 'right' }),
    render: ({ ledgerItem, setting }) => NumberFormatter.formatCurrency(ledgerItem.currency || setting.default_currency, setting.number_format, ledgerItem.net_total)
  },
  tax_total: {
    header: () => ({ title: i18n.t('LedgerItem::tax_total'), align: 'right' }),
    render: ({ ledgerItem, setting }) => NumberFormatter.formatCurrency(ledgerItem.currency || setting.default_currency, setting.number_format, ledgerItem.tax_total)
  },
  amount: {
    header: () => ({ title: i18n.t('LedgerItem::amount'), align: 'right' }),
    render: ({ ledgerItem, setting }) => NumberFormatter.formatCurrency(ledgerItem.currency || setting.default_currency, setting.number_format, ledgerItem.amount)
  },
  notes: {
    header: () => ({ title: i18n.t('LedgerItem::notes') }),
    render: ({ ledgerItem }) => <div dangerouslySetInnerHTML={{ __html: showdown.makeHtml(ledgerItem.notes || '-') }} />
  },
  reminders_enabled: {
    header: () => ({ title: i18n.t('LedgerItem::reminders_enabled') }),
    render: ({ ledgerItem }) => ledgerItem.reminders_enabled ? i18n.t('LedgerItem::Yes') : i18n.t('LedgerItem::No')
  },
  created_at: {
    header: () => ({ title: i18n.t('LedgerItem::created_at') }),
    render: ({ ledgerItem, setting }) => ledgerItem.created_at ? moment(ledgerItem.created_at).format(setting.date_format) : '-'
  },
  updated_at: {
    header: () => ({ title: i18n.t('LedgerItem::updated_at') }),
    render: ({ ledgerItem, setting }) => ledgerItem.updated_at ? moment(ledgerItem.updated_at).format(setting.date_format) : '-'
  },
}

export function getInvoiceTableRenderer(options: ResourceRendererOptions<LedgerItem>): ResourceRendererResult<LedgerItem, LedgerItemProperty> {
  const {
    onRowClick,
    selectedItems = [],
    onSelectionChange,
    onActionClick
  } = options

  const columns: { label: string, value: string }[] = InvoiceColumns.map(column => ({ label: i18n.t(`LedgerItem::${column}`), value: column }))
  const selectedColumns: LedgerItemProperty[] = LocalStorage
    .get(LocalStorageKey.INVOICE_COLUMNS, DEFAULT_INVOICE_COLUMNS)
    .filter((column: InvoiceColumnProperty) => InvoiceColumnConfigs[column])

  const headers: ResourceTableHeader[] =
    [
      {
        title: '',
      },
      ...selectedColumns
        .map(column => ({
          ...InvoiceColumnConfigs[column].header(),
          visible: true
        })),
      {
        title: '',
        stickyRight: '0px'
      }
    ]

  const renderRow = (ledgerItem: LedgerItem, _index: number): React.ReactElement => {
    const isSelected = selectedItems.includes(ledgerItem.id)
    const isPaid = ledgerItem.paid_on !== null
    let actions: IActionListItem[] = [
      { key: 'mark_paid', icon: 'money-bill-wave', content: i18n.t('LedgerItemsTable::Mark as paid'), visible: !isPaid },
      { key: 'send', icon: 'send', content: i18n.t('LedgerItemsTable::Send') },
      { key: 'send_peppol', icon: 'send', content: i18n.t('LedgerItemsTable::Send via peppol') },
      { key: 'create_credit_note', icon: 'invoice', content: i18n.t('LedgerItemsTable::Create credit note') },
      { key: 'view', icon: 'eye', content: i18n.t('LedgerItemsTable::View') },
      { key: 'duplicate', icon: 'duplicate', content: i18n.t('LedgerItemsTable::Duplicate') },
      { key: 'edit', icon: 'edit', content: i18n.t('LedgerItemsTable::Edit') },
      { key: 'download', icon: 'pdf', content: i18n.t('LedgerItemsTable::Download PDF'), visible: Boolean(ledgerItem.pdf_attachment_download_url) },
      { key: 'download_ubl', icon: 'xml', content: i18n.t('LedgerItemsTable::Download UBL'), visible: Boolean(ledgerItem.pdf_attachment_download_url) },
      { key: 'delete', icon: 'trash-alt-solid', content: i18n.t('LedgerItemsTable::Delete'), destructive: true },
    ]

    const applyMaxWidthColumns: LedgerItemProperty[] = [
      'contact',
      'project',
      'number',
      'subject',
      'notes'
    ]

    let cells: React.ReactNode[] =
      [
        <ResourceTableRowData onClick={onRowClick ? () => onRowClick(ledgerItem) : undefined} maxWidth='30px' maxWidthMobile='50px'>
          <LedgerItemAvatar width={30} ledgerItem={ledgerItem} rounded={true} onClick={() => this.onTableLedgerItemClick(ledgerItem)} />
        </ResourceTableRowData>,
        ...selectedColumns.map((column) => (
          <ResourceTableRowData
            key={`${ledgerItem.id}-${column}`}
            onClick={onRowClick ? () => onRowClick(ledgerItem) : undefined}
            maxWidth={applyMaxWidthColumns.includes(column) ? '300px' : undefined}
            ellipse={applyMaxWidthColumns.includes(column)}
            textAlign={InvoiceColumnConfigs[column].header().align}
          >
            {InvoiceColumnConfigs[column].render({ ledgerItem, ...options })}
          </ResourceTableRowData>
        ))
      ]


    // Add actions cell if actions are provided
    if (actions?.length > 0 && onActionClick) {
      cells = [
        ...cells,
        <ResourceTableRowActions
          key={`${ledgerItem.id}-actions`}
          actions={actions}
          onActionClick={(key) => onActionClick(key, ledgerItem)}
          sticky={true}
          stickyRight='0px'
        />
      ]
    }

    return (
      <ResourceTableRow
        key={ledgerItem.id}
        selected={isSelected}
        onSelectionChange={onSelectionChange ? (selected) => onSelectionChange(selected, ledgerItem.id) : undefined}
      >
        {cells}
      </ResourceTableRow>
    )
  }

  return {
    headers: headers,
    columns: columns,
    selectedColumns: selectedColumns,
    renderRow: renderRow,
  }
}

export default getInvoiceTableRenderer
